/* eslint-disable max-statements */
import { Address, PartialAddress } from '@dreryk/address-common-api';
import {
   GuardianAlreadyExistsFault,
   GuardianCreateCommand,
   GuardianSameAsPatientFault,
   GuardianSimpleQuery,
   GuardianUpdateCommand,
} from '@dreryk/egabinet-patient-frontend-api';
import { FirstName, LastName } from '@dreryk/person-name-common-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { createSingleInterpolatedTranslationKey } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { guardiansTranslationKeys } from '../../../translations';
import { createGuardianExternalIdentifier } from '../GuardianDetailsModal/GuardianDetailsModal';
import { GuardianModalFormBean, GuardianModalFormController, StatutoryRepresentative } from '../GuardianModal.types';

// eslint-disable-next-line max-lines-per-function
export const useGuardianCreateOrUpdateCommand = (
   patientId: number | undefined,
   guardianId: number | undefined,
   setError: GuardianModalFormController['setError'],
   onSuccess: ((parameters: GuardianModalFormBean) => void) | undefined
): UseCqrsCommandResult<void, GuardianModalFormBean> => {
   return useCqrsCommand<void, GuardianModalFormBean, GuardianAlreadyExistsFault | GuardianSameAsPatientFault>(
      (parameters) => {
         const command = (() => {
            if (guardianId) {
               const guardianUpdateCommand = new GuardianUpdateCommand();
               guardianUpdateCommand.id = guardianId;
               return guardianUpdateCommand;
            }
            const guardianCreateCommand = new GuardianCreateCommand();
            guardianCreateCommand.patientId = patientId!;
            return guardianCreateCommand;
         })();
         const { relationCode, statutoryRepresentative } = parameters;
         const guardian = parameters.guardian!;
         /* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
         command.firstName = FirstName.of(guardian!.firstName!);
         command.lastName = LastName.of(guardian!.lastName!);
         command.guardianExternalIdentifier = createGuardianExternalIdentifier(guardian);
         command.relation = relationCode;
         command.statutoryRepresentative = StatutoryRepresentative.YES === statutoryRepresentative;

         command.address = Choice.of<Address>(
            PartialAddress.builder()
               .withCountry(guardian.country)
               .withPostcode(guardian.postcode!)
               .withTown(guardian.town!)
               .withStreet(guardian.street!)
               .withBuilding(guardian.house!)
               .withFlat(guardian.flat!)
               .build()
         );

         command.phoneNumber = Choice.withUseDefaultIfNull(guardian!.phoneNumber ?? null);
         command.emailAddress = Choice.withUseDefaultIfNull(guardian!.emailAddress ?? null);
         return command;
         /* eslint-enable */
      },
      {
         onSuccess: (result, parameters) => {
            const successMessage = createSingleInterpolatedTranslationKey(
               guardiansTranslationKeys.guardianDataSaved,
               'fullName',
               // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
               `${parameters.guardian!.firstName} ${parameters.guardian!.lastName}`
            );
            dispatchSnackbarPositiveEvent(successMessage);
            refetchCqrsQuery(GuardianSimpleQuery);
            onSuccess?.(parameters);
         },
         onFault: {
            'egabinet-patient-frontend-api:GuardianAlreadyExistsFault': () => {
               dispatchSnackbarNegativeEvent(guardiansTranslationKeys.guardianAlreadyExist);
            },
            'egabinet-patient-frontend-api:GuardianSameAsPatientFault': () => {
               setError('guardian', { message: guardiansTranslationKeys.guardianSameAsPatient });
            },
         },
      }
   );
};
